import { Provider, Type, inject } from '@angular/core';
import { Params } from '@angular/router';
import { Address, CampaignTariffSelectionData, PreisIndikation, Product, PushPdfInfo, TariffChangeAvailabilities } from '@mwe/models';
import { FeatureToggleEnum } from '@mwe/constants';
import { ProfileService } from '../profile/profile.service';

/**
 * Unified interface for both the V1 {@link TariffChangeLogic} and the V2
 * {@link OrderTariffChangeLogic} implementations.
 *
 * External callers (outside the tariff-change process internals) should inject
 * {@link AbstractTariffChangeLogic} as the token and program against this class only.
 * The factory provided by {@link provideTariffChangeLogic} selects the concrete
 * implementation at runtime based on {@link FeatureToggleEnum.TARIFF_CHANGE_V2_ENABLED}.
 */
export abstract class AbstractTariffChangeLogic {
  /**
   * Primary external entry point.
   * Writes the pre-selected address and products into the respective state
   * storage (V1: TariffChangeStateService, V2: TariffChangeStore) and navigates
   * directly to the products step, bypassing address selection.
   *
   * Replaces ad-hoc TariffChangeStateService writes in ProductsGroupComponent
   * and ProductDetailsLogic.
   */
  abstract initProcess(address?: Address, products?: Product[], preselectedProduct?: Product): void;

  /**
   * Parse predefined tariff / voucher URL query parameters and persist them.
   * Called by entry components (e.g. secure-offer wizard) before the first step.
   */
  abstract initFromUrlParams(queryParams: Params): void;

  /** Write initial tracking data on first process entry. */
  abstract trackInitialData(eventName?: string): void;

  /**
   * Discard all per-address derived state (availabilities, order details, voucher, …)
   * so that navigating back and picking a different address starts fresh.
   */
  abstract clearStateAfterAddressChange(): void;

  /**
   * Fetch tariff-change availabilities for the pre-set products and persist the
   * result into the state storage.
   */
  abstract loadAvailabilities(): Promise<TariffChangeAvailabilities>;

  abstract loadPriceIndicationsForAvailabilities(zipCode: string): Promise<PreisIndikation[]>;

  /**
   * Load and encrypt per-product order-detail data and persist it into state
   * storage.
   */
  abstract loadOrderDetails(address: Address): Promise<void>;

  /**
   * Populate the shared TariffSelectionStore ready for the products-selection
   * step.
   */
  abstract initTariffSelectionStep(allesSicherGroupName: string, zipCode: string): Promise<void>;

  /** Derive invoice / e-invoice data from the loaded order details and persist it. */
  abstract loadInvoiceData(): void;

  /**
   * Commit the current tariff selection from TariffSelectionCardLogic back into
   * the process state storage.
   */
  abstract commitTariffSelection(): void;

  /** Reset shared selection sub-services (e.g. TariffSelectionCardLogic). */
  abstract resetSelectionServices(): void;

  abstract checkForMissingSAPMail(): boolean;

  abstract loadCampaignData(): Promise<CampaignTariffSelectionData | null>;

  abstract loadSpotData(): Promise<Record<string, PushPdfInfo> | null>;

  /**
   * Persist the id of the last submitted order so the status page can load its
   * details. The concrete implementation writes to the storage matching its
   * version (V1: {@link TariffChangeStateService.lastOrderId},
   * V2: {@link TariffChangeStore} confirmation-status state), keeping external
   * callers (e.g. recent-order navigation) agnostic of the active version.
   */
  abstract setLastOrderId(lastOrderId: string): void;
}

/**
 * Creates an Angular DI {@link Provider} for {@link AbstractTariffChangeLogic}.
 *
 * The factory selects between the V1 and V2 logic implementations at runtime,
 * driven by {@link FeatureToggleEnum.TARIFF_CHANGE_V2_ENABLED}.
 *
 * Register once in the root providers of each app that uses the tariff-change process:
 *
 * ```ts
 * // app.config.ts
 * import { TariffChangeLogic } from '@mwe/features/tariff-change';
 * import { OrderTariffChangeLogic } from '@mwe/features/order-process/tariff-change';
 *
 * provideTariffChangeLogic(TariffChangeLogic, OrderTariffChangeLogic)
 * ```
 *
 * @param v1 The V1 concrete class (TariffChangeLogic). Must be injectable (providedIn root or
 *   listed in enclosing providers).
 * @param v2 The V2 concrete class (OrderTariffChangeLogic). Must be injectable.
 */
export function provideTariffChangeLogic(v1: Type<AbstractTariffChangeLogic>, v2: Type<AbstractTariffChangeLogic>): Provider {
  return {
    provide: AbstractTariffChangeLogic,
    useFactory: () => {
      const profileService = inject(ProfileService);
      return profileService.isFeatureToggleEnabled(FeatureToggleEnum.TARIFF_CHANGE_V2_ENABLED) ? inject(v2) : inject(v1);
    },
  };
}
